import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import QMSLayout from "../components/QMSLayout";
import { API, useAuth } from "../App";
import { useTranslation } from "react-i18next";
import {
  Activity, Shield, ClipboardCheck, Bell, Users,
  Siren, PenTool, FileText, BarChart3, Upload,
  MessageSquare, CheckCircle2, FolderOpen,
  ArrowLeft, ChevronRight, ChevronDown, Lightbulb,
  BookOpen, Play, CircleCheck
} from "lucide-react";

const SECTION_ICONS = {
  systemHealth: Activity,
  governance: Shield,
  qaSupervisor: ClipboardCheck,
  notifications: Bell,
  onboardingReview: Users,
  auditConductor: Siren,
  checklist: ClipboardCheck,
  scriber: PenTool,
  findingsCapa: FileText,
  reportsExport: BarChart3,
  myOnboarding: ClipboardCheck,
  uploadArtifacts: Upload,
  reviewRequests: MessageSquare,
  readinessStatus: CheckCircle2,
  helpCenter: FolderOpen,
};

const SECTION_COLORS = {
  systemHealth: "#4ADE80",
  governance: "#36A9E1",
  qaSupervisor: "#E8A838",
  notifications: "#A78BFA",
  onboardingReview: "#F472B6",
  auditConductor: "#F87171",
  checklist: "#36A9E1",
  scriber: "#4ADE80",
  findingsCapa: "#E8A838",
  reportsExport: "#A78BFA",
  myOnboarding: "#36A9E1",
  uploadArtifacts: "#4ADE80",
  reviewRequests: "#E8A838",
  readinessStatus: "#A78BFA",
  helpCenter: "#F472B6",
};

const ROLE_SECTIONS = {
  admin: ["systemHealth", "governance", "qaSupervisor", "notifications", "onboardingReview"],
  auditor: ["auditConductor", "checklist", "scriber", "findingsCapa", "reportsExport"],
  client: ["myOnboarding", "uploadArtifacts", "reviewRequests", "readinessStatus", "helpCenter"],
};

const SECTION_PATHS = {
  systemHealth: "/dashboard",
  governance: "/governance",
  qaSupervisor: "/qa-supervisor",
  notifications: "/notification-log",
  onboardingReview: "/client-portal",
  auditConductor: "/audit-conductor",
  checklist: "/audits",
  scriber: "/scriber",
  findingsCapa: "/capa",
  reportsExport: "/reports",
  myOnboarding: "/client-onboarding",
  uploadArtifacts: "/documents",
  reviewRequests: "/messages",
  readinessStatus: "/client-portal",
  helpCenter: "/knowledge",
};

const GuidePage = () => {
  const { role: urlRole } = useParams();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(["guide", "common"]);
  const [expandedSection, setExpandedSection] = useState(null);
  const [completedSections, setCompletedSections] = useState([]);
  const [guideState, setGuideState] = useState(null);

  const userRole = user?.role || "client";
  // Enforce role isolation: only admin can view other roles' guides
  const effectiveRole = isAdmin ? (urlRole || userRole) : userRole;
  const sections = ROLE_SECTIONS[effectiveRole] || ROLE_SECTIONS.client;

  const fetchGuideState = useCallback(async () => {
    try {
      const res = await fetch(`${API}/user-guide/state`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setGuideState(data);
        setCompletedSections(data.completed_cards || []);
      }
    } catch (err) {
      console.error("Guide state fetch failed:", err);
    }
  }, []);

  useEffect(() => { fetchGuideState(); }, [fetchGuideState]);

  const toggleSection = (sectionKey) => {
    setExpandedSection(expandedSection === sectionKey ? null : sectionKey);
  };

  const markSectionComplete = async (sectionKey) => {
    const updated = completedSections.includes(sectionKey)
      ? completedSections
      : [...completedSections, sectionKey];
    setCompletedSections(updated);
    try {
      await fetch(`${API}/user-guide/state`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...guideState,
          completed_cards: updated,
          status: updated.length === sections.length ? "completed" : "in_progress",
        }),
      });
    } catch (err) {
      console.error("Guide state update failed:", err);
    }
  };

  const progress = Math.round((completedSections.filter(sc => sections.includes(sc)).length / sections.length) * 100);

  return (
    <QMSLayout title={t("guide:page.title")}>
      <div className="max-w-4xl mx-auto" data-testid="guide-page">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-1.5 text-[#8B95A5] hover:text-white text-sm mb-4 transition-colors"
            data-testid="guide-back-btn"
          >
            <ArrowLeft size={14} />
            {t("guide:page.backToDashboard")}
          </button>
          <h1 className="font-['Syne'] text-2xl font-bold text-white flex items-center gap-3" data-testid="guide-page-title">
            <BookOpen className="w-6 h-6 text-[#36A9E1]" />
            {t(`guide:page.${effectiveRole}.title`)}
          </h1>
          <p className="text-[#8B95A5] text-sm mt-1">{t(`guide:page.${effectiveRole}.subtitle`)}</p>
        </div>

        {/* Progress */}
        <div className="mb-6 p-4 rounded-lg border border-[rgba(255,255,255,0.08)] bg-[#1A1D27]" data-testid="guide-page-progress">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white text-sm font-medium">Progress</span>
            <span className="text-[#8B95A5] text-xs font-mono">{progress}%</span>
          </div>
          <div className="h-1.5 bg-[rgba(255,255,255,0.06)] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${progress}%`,
                background: progress === 100
                  ? "linear-gradient(90deg, #4ADE80, #36A9E1)"
                  : "linear-gradient(90deg, #36A9E1, #A78BFA)",
              }}
            />
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-3" data-testid="guide-sections">
          {sections.map((sectionKey) => {
            const Icon = SECTION_ICONS[sectionKey];
            const color = SECTION_COLORS[sectionKey];
            const isExpanded = expandedSection === sectionKey;
            const isComplete = completedSections.includes(sectionKey);
            const sectionPath = `guide:page.${effectiveRole}.sections.${sectionKey}`;
            const steps = t(`${sectionPath}.steps`, { returnObjects: true });
            const stepsArr = Array.isArray(steps) ? steps : [];

            return (
              <div
                key={sectionKey}
                className={`rounded-lg border transition-all duration-200 ${
                  isComplete
                    ? "border-[rgba(74,222,128,0.2)] bg-[rgba(74,222,128,0.03)]"
                    : "border-[rgba(255,255,255,0.08)] bg-[#1A1D27]"
                }`}
                data-testid={`guide-section-${sectionKey}`}
              >
                {/* Section header */}
                <button
                  onClick={() => toggleSection(sectionKey)}
                  className="w-full flex items-center gap-3 p-4 text-left"
                  data-testid={`guide-section-toggle-${sectionKey}`}
                >
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${color}15` }}
                  >
                    {isComplete ? (
                      <CircleCheck size={18} className="text-[#4ADE80]" />
                    ) : (
                      <Icon size={18} style={{ color }} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white text-sm font-semibold">{t(`${sectionPath}.title`)}</h3>
                    <p className="text-[#8B95A5] text-xs mt-0.5 line-clamp-1">{t(`${sectionPath}.description`)}</p>
                  </div>
                  {isExpanded ? (
                    <ChevronDown size={16} className="text-[#8B95A5] shrink-0" />
                  ) : (
                    <ChevronRight size={16} className="text-[#8B95A5] shrink-0" />
                  )}
                </button>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-[rgba(255,255,255,0.05)]">
                    <div className="pt-3 space-y-2">
                      {stepsArr.map((step, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <span className="w-5 h-5 rounded-full bg-[rgba(54,169,225,0.1)] text-[#36A9E1] text-xs flex items-center justify-center shrink-0 mt-0.5 font-mono">
                            {idx + 1}
                          </span>
                          <p className="text-[#CBD5E1] text-sm leading-relaxed">{step}</p>
                        </div>
                      ))}
                    </div>

                    {/* Tip */}
                    <div className="mt-3 flex items-start gap-2 p-3 rounded-md bg-[rgba(232,168,56,0.06)] border border-[rgba(232,168,56,0.12)]">
                      <Lightbulb size={14} className="text-[#E8A838] shrink-0 mt-0.5" />
                      <p className="text-[#E8A838] text-xs leading-relaxed">{t(`${sectionPath}.tip`)}</p>
                    </div>

                    {/* Actions */}
                    <div className="mt-3 flex items-center gap-2">
                      <button
                        onClick={() => navigate(SECTION_PATHS[sectionKey])}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-[#36A9E1] hover:bg-[#2B8BBD] transition-colors"
                        data-testid={`guide-go-${sectionKey}`}
                      >
                        <Play size={12} />
                        {t("common:actions.start")}
                      </button>
                      {!isComplete && (
                        <button
                          onClick={() => markSectionComplete(sectionKey)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-[#4ADE80] bg-[rgba(74,222,128,0.08)] hover:bg-[rgba(74,222,128,0.15)] transition-colors"
                          data-testid={`guide-complete-${sectionKey}`}
                        >
                          <CheckCircle2 size={12} />
                          Mark Complete
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </QMSLayout>
  );
};

export default GuidePage;
